import { mediaTypes, typeItems } from "../config/fields";
import { findTriggerEvent } from "./commonUtils";

/**
 * Filters and returns only functional transactions.
 *
 * A transaction is considered functional if:
 * - It has a valid trigger event
 * - The trigger event source is NOT a controller-type clusterlet
 *
 * Safely handles missing or invalid inputs and returns an empty array on failure.
 */
export const getFunctionalTransactions = ({ transactions = [], events = [], clusterlets = [] }) => {
  try {
    if (!Array.isArray(transactions) || !Array.isArray(events) || !Array.isArray(clusterlets)) {
      return [];
    }

    return transactions.filter(tr => tr.type === 7).filter(transaction => {
      const triggerEvent = findTriggerEvent({ transaction, events, clusterlets });
      if (!triggerEvent?.src) return false;

      const triggerEventSource = clusterlets.find(
        clusterlet => clusterlet._id === triggerEvent.src
      );

      // Exclude controller-triggered transactions
      return triggerEventSource?.subType !== "controller";
    });
  } catch (error) {
    console.error("Finding functional transactions failed.", error);
    return [];
  }
};

export const getDataFetchTransactions = ({ transactions = [], events = [], clusterlets = [] }) => {
  try {
    if (!Array.isArray(transactions) || !Array.isArray(events) || !Array.isArray(clusterlets)) {
      return [];
    }

    return transactions.filter(transaction => {
      const triggerEvent = findTriggerEvent({ transaction, events, clusterlets });
      if (!triggerEvent?.src) return false;

      const triggerEventSource = clusterlets.find(
        clusterlet => clusterlet._id === triggerEvent.src
      );

      // Exclude controller-triggered transactions
      return triggerEventSource?.subType == "controller";
    });
  } catch (error) {
    console.error("Finding fetch transactions failed.", error);
    return [];
  }
};

export const getTransactionClusterlets = ({transaction, clusterlets}) => {
  let transactionClusterlets = [];
    transaction?.clusterlets.sort((a, b) => a.order - b.order)
    transaction?.clusterlets.forEach((clusterlet, index) => {
      clusterlets.forEach((element, index) => {
        if (clusterlet.id === element._id) {
          transactionClusterlets.push({ id: element._id, label: element.name, type: element.type, icon:"", ...element });
        }
      });
    });

    return transactionClusterlets;
}

/**
 * Calculates the total number of elements to be rendered in a transaction diagram.
 *
 * Each item contributes to the overall count based on its type and expansion state.
 *
 * Rules:
 * - Every item (event or transaction) adds 1 to the count.
 * - If the item is of type "nested", it adds an extra 1 for its container.
 * - If the "nested" item is expanded (based on `expandItems`) and has children:
 *   - Recursively adds the count of its children.
 * - If the expanded "nested" item has no children, it adds 2 more (as padding/placeholder).
 *
 * @param {Object[]} transactionElements - Array of transaction elements (events or nested).
 * @param {Object|null} expandItems - Map of expanded item IDs, e.g., { [id]: true }.
 * @returns {number} Total count of elements to be rendered.
 */
export const getTransactionElementsCount = ({ transactionElements, expandItems = null }) => {
  let count = 1; // base line or root item

  transactionElements?.forEach(item => {
    count += 1; // count this item

    if (item?.type === "nested") {
      count += 1; // additional slot for container/representation

      if (expandItems?.[item?.id]) {
        if (item?.children?.length > 0) {
          count += getTransactionElementsCount({ transactionElements: item.children, expandItems });
        } else {
          count += 2; // placeholder rows when expanded but no children
        }
      }
    }
  });

  return count;
};


export const findTransactionsWithoutSrcDst = (transactionId, transactions, events, srcId, dstId) => {

    let transactionItem = transactions.find(item => item._id === transactionId);
    transactionItem.forEach(ev => {
      const fullEvent = events.find(e => e._id === ev.id);
      if(fullEvent){

      }
    })    
  
}


export const findUnmatchedTransacstions = (majorTransaction, transactions, events, srcId, dstId) => {
  const transactionMap = new Map(transactions.map(t => [t.id, t]));
  const eventMap = new Map(events.map(e => [e._id, e]));

  const result = [];

  function traverseAndCollect(tx) {
    if (!tx || !tx.nestedTransactions) return;

    for (const nested of tx.nestedTransactions) {
      const childTx = transactionMap.get(nested.id);
      if (!childTx) continue;

      if (childTx.type === 9) {
        const txEvents = childTx.events || [];

        const foundInScope = txEvents.some(evRef => {
          const event = eventMap.get(evRef.id);
          if (!event) return false;

          return (
            event.src === srcId ||
            event.dst === srcId ||
            event.src === dstId ||
            event.dst === dstId
          );
        });

        if (!foundInScope) {
          result.push(childTx); // It's out of scope, include it
        }
      }

      // Keep traversing for deeper nesting
      traverseAndCollect(childTx);
    }
  }

  traverseAndCollect(majorTransaction);

  return result;
}

export const findUnmatchedTransactions = (majorTransaction, transactions, events, srcId, dstId) =>  {
  const transactionMap = new Map(transactions.map(t => [t.id, t]));
  const eventMap = new Map(events.map(e => [e._id, e]));

  const result = [];

  function isInScope(tx) {
    let hasSrc = false;
    let hasDst = false;

    for (const evRef of tx.events || []) {
      const event = eventMap.get(evRef.id);
      if (!event) continue;

      if (event.src === srcId || event.dst === srcId) {
        hasSrc = true;
      }
      if (event.src === dstId || event.dst === dstId) {
        hasDst = true;
      }

      if (hasSrc && hasDst) return true; // both found, in scope
    }

    return false; // if either src or dst not found, out of scope
  }

  function collectOutOfScopeNested(tx) {
    if (!tx || !tx.nestedTransactions) return;

    for (const nested of tx.nestedTransactions) {
      const nestedTx = transactionMap.get(nested.id);
      if (!nestedTx) continue;

      if (nestedTx.type === 9 && !isInScope(nestedTx)) {
        result.push(nestedTx);
      }

      // recursive for deeper nesting
      collectOutOfScopeNested(nestedTx);
    }
  }

  collectOutOfScopeNested(majorTransaction);

  return result;
}



export function isTransactionInScope(transactionId, transactions, events, srcId, dstId, majorTransaction) {
  const transaction = transactions.find(t => t._id === transactionId);
  if (!transaction || !majorTransaction) return false;

  const eventMap = new Map(events.map(e => [e._id, e]));
  const clusterletOrderMap = new Map(
    (majorTransaction.clusterlets || []).map(c => [c.id, c.order])
  );

  const involvedClusterlets = new Set();

  // 1. Collect involved clusterlets from events
  for (const evRef of transaction.events || []) {
    const event = eventMap.get(evRef.id);
    if (!event) continue;

    if (event.src) involvedClusterlets.add(event.src);
    if (event.dst) involvedClusterlets.add(event.dst);
  }

  // 2. Expand involvedClusterlets to include in-between clusterlets based on order
  const involvedOrders = Array.from(involvedClusterlets)
    .map(id => clusterletOrderMap.get(id))
    .filter(order => order !== undefined)
    .sort((a, b) => a - b);

  if (involvedOrders.length >= 2) {
    const minOrder = involvedOrders[0];
    const maxOrder = involvedOrders[involvedOrders.length - 1];

    for (const [id, order] of clusterletOrderMap.entries()) {
      if (order > minOrder && order < maxOrder) {
        involvedClusterlets.add(id);
      }
    }
  }

  // 3. Final scope check
  return involvedClusterlets.has(srcId) && involvedClusterlets.has(dstId);
}


/**
 * Calculate the width of a given text string for a specific font style.
 *
 * @param {string} text - The text to measure.
 * @param {string} [font='12px Arial'] - The CSS font property (size and family).
 * @param {boolean} [toUpperCase=false] - Convert text to uppercase before measuring.
 * @returns {number} - The width of the text in pixels.
 */
export function getTextWidth(text, font = '12px Arial', toUpperCase = false) {
  if (typeof text !== 'string') {
    console.warn('getTextWidth: Provided text is not a string');
    return 0;
  }

  // Reuse a single canvas for performance
  const canvas = getTextWidth._canvas || (getTextWidth._canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  if (!context) return 0;

  context.font = font;
  const measuredText = toUpperCase ? text.toUpperCase() : text;
  return context.measureText(measuredText).width;
}



export const findNestedTransactions = ({transactions, parent}) => {
  try {

    let nestedTransactions = [];

    if(parent?.nestedTransactions == undefined || parent?.nestedTransactions?.length === 0){
        return [];
    }

    const findNested = (parentTxn) => {
        if(parentTxn?.nestedTransactions && parentTxn?.nestedTransactions?.length > 0){
            const nestedItemIds = parentTxn?.nestedTransactions?.map(item => item.id)
            // console.log("nested nestedItemIds: ", nestedItemIds)

            const nestedItems = transactions?.filter(item =>  nestedItemIds?.includes(item?._id.toString()))
            if(nestedItems && nestedItems?.length > 0){
                nestedItems?.forEach(item => {
                    findNested(item)
                })
                nestedTransactions = [...nestedTransactions, ...nestedItems]
            }else{
                throw new Error(`Finding transactions with ids ${nestedItemIds.join(", ")} failed`);
            }


        } 

    }

    findNested(parent);

    return nestedTransactions;
  } catch (error) {
    console.error(`Finding nested transactions failed. ${error}`);
    return []
  }
}

export const findTransactionEvents = ({transaction, allEvents}) => {
  try {
    return transaction?.events?.map(event => {

      const eventObj = allEvents?.find(item => item?._id === event?.id);

      return eventObj;
    
    }) || [];

  } catch (error) {
    console.error(`Finding events failed. ${error}`);
    return [];
  }
}


export const filterQuickViewMessages = ({ quickViewMessages = [], events = [], messages = [],}) => {
  try {
    if (!Array.isArray(quickViewMessages) || !Array.isArray(events)) {
      return quickViewMessages;
    }

    const eventIds = new Set(events.map(item => item._id));

    const filteredMessages = quickViewMessages.filter(msg =>
      eventIds.has(msg.message.eventId)
    )?.map(msg => {
      const message = messages?.find(item => item?.eventId === msg.message.eventId);
      if(message){
        msg.message = message;
      }

      return msg;
    })

    return filteredMessages;


    // return quickViewMessages;

  } catch (error) {
    console.error(`Filtering quick view messages list failed. ${error}`);
    return quickViewMessages;
  }
};


export function buildMessageStructure({ transactions = [], allTransactions = [], allEvents = [], allMessages = [], allFields = []}) {
  try {
    /* -------------------- Pre-index lookups (O(1)) -------------------- */
    const eventMap = new Map(allEvents.map(e => [e._id, e]));
    const messageMap = new Map(allMessages.map(m => [m._id, m]));
    const transactionMap = new Map(allTransactions.map(t => [t._id, t]));

    const fieldsByMessage = new Map();
    const fieldsByField = new Map();

    allFields.forEach(field => {
      field?.parent?.forEach(p => {
        if (p.type === "message") {
          if (!fieldsByMessage.has(p.id)) fieldsByMessage.set(p.id, []);
          fieldsByMessage.get(p.id).push(field);
        }
        if (p.type === "field") {
          if (!fieldsByField.has(p.id)) fieldsByField.set(p.id, []);
          fieldsByField.get(p.id).push(field);
        }
      });
    });

    /* ------------------------ Shared helpers ------------------------ */
    const resolveReadableType = (type) =>
      typeItems.find(i => i.value === type)?.title ||
      mediaTypes.find(i => i.value === type)?.title ||
      messageMap.get(type)?.name ||
      type;

    const buildFieldNode = (field) => ({
      fieldId: field._id,
      fieldName: field.name,
      attributes: field.attributes,
      type: field.type,
      readableType: resolveReadableType(field.attributes?.type),
      children: (fieldsByField.get(field._id) || []).map(buildFieldNode)
    });

    const buildFieldTree = (messageId) =>
      (fieldsByMessage.get(messageId) || []).map(buildFieldNode);

    /* -------------------- Main processing -------------------- */
    const structuredMessages = [];
    const visitedTransactions = new Set();

    const processTransaction = (transaction) => {
      if (!transaction || visitedTransactions.has(transaction._id)) return;
      visitedTransactions.add(transaction._id);

      // Messages from events
      transaction?.events?.forEach(eventRef => {
        const event = eventMap.get(eventRef.id);
        if (!event) return;

        const message = messageMap.get(event.message);
        if (!message) return;

        structuredMessages.push({
          eventId: event._id,
          eventName: event.name,
          messageId: message._id,
          fieldName: message.name,
          children: buildFieldTree(message._id)
        });
      });

      // Nested transactions
      transaction?.nestedTransactions?.forEach(nested => {
        const nestedTx = transactionMap.get(nested.id);
        if (nestedTx) processTransaction(nestedTx);
      });
    };

    transactions.forEach(processTransaction);

    return structuredMessages;
  } catch (error) {
    console.error("Building message structure failed.", error);
    return [];
  }
}
